import { useContext, useEffect, useState } from 'react';
import { FeatureFlagContext } from '@/providers/FeatureFlagContext';
import type { EFeatureFlag } from '@/lib/feature-flags/keys';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { useUIStore } from '@/stores/ui.store';
import { usePermissions } from '@/lib/permissions';
import { EUserRole, getRoleDisplayName } from '@/types';
import {
  getNavItemsForUser,
  getSidebarGroups,
  type NavGroup,
  type NavItem,
  type NavSubItem,
} from './sidebar-config';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { useTenantLogo } from '@/hooks/useTenantLogo';
import { useHierarchyLabelMap } from '@/features/operations/hooks/useHierarchyLabels';

export function Sidebar() {
  const user = useAuthStore((s) => s.user);
  const activeRole = useAuthStore((s) => s.activeRole);
  const activePortal = useAuthStore((s) => s.activePortal);
  const logo = useTenantLogo();
  const { sidebarCollapsed, toggleSidebarCollapse, sidebarOpen, setSidebarOpen } = useUIStore();
  // Tenant-customisable hierarchy labels — Sidebar items like "Site Hierarchy"
  // rewrite to the tenant's chosen singular ("Concession Hierarchy").
  const hierarchyLabels = useHierarchyLabelMap();
  const location = useLocation();

  const ffCtx = useContext(FeatureFlagContext);
  // If the flags could not be fetched, show the menu and let the API decide.
  const flagEnabled = (flag: EFeatureFlag) => ffCtx.isError || ffCtx.enabled[flag];
  const { can } = usePermissions();
  const navItems = getNavItemsForUser(
    activePortal,
    hierarchyLabels,
    activeRole ?? EUserRole.MINE_DIRECTOR,
    flagEnabled,
    can,
  );
  const navGroups = getSidebarGroups(activePortal, navItems);

  // Close the mobile drawer whenever the route changes so a nav tap doesn't
  // leave it hanging open over the new page.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname, setSidebarOpen]);

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile-only backdrop — closes the drawer on outside tap. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[39] bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        role="navigation"
        aria-label="Main navigation"
        className={cn(
          'fixed left-0 top-0 z-40 flex h-screen w-64 flex-col border-r bg-sidebar transition-all duration-300',
          sidebarCollapsed ? 'lg:w-16' : 'lg:w-64',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        )}
      >
        {/* Logo + Tenant */}
        <div className="border-b px-3 py-3 overflow-hidden">
          {!sidebarCollapsed ? (
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <div className="h-10 -ml-3 overflow-hidden flex items-center">
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    onError={logo.onError}
                    className={
                      logo.isCustom
                        ? 'h-9 w-auto max-w-[8.5rem] object-contain'
                        : 'h-36 w-auto object-contain'
                    }
                  />
                </div>
                {user?.tenantName && (
                  <p className="truncate text-[11px] text-text-body mt-0.5 ml-0.5">{user.tenantName}</p>
                )}
              </div>
              <button
                onClick={toggleSidebarCollapse}
                aria-label="Collapse sidebar"
                className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground shrink-0"
              >
                <ChevronLeft className="size-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="h-8 overflow-hidden flex items-center justify-center">
                <img
                  src={logo.src}
                  alt={logo.alt}
                  onError={logo.onError}
                  className={
                    logo.isCustom
                      ? 'size-8 object-contain'
                      : 'h-28 w-auto object-contain'
                  }
                />
              </div>
              <button
                onClick={toggleSidebarCollapse}
                aria-label="Expand sidebar"
                className="rounded-md p-1.5 mt-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <ChevronLeft className={cn('size-4 rotate-180')} />
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin p-2 space-y-0.5">
          {navGroups.map((group, index) =>
            // Collapsed, the sidebar is an icon rail. Nesting there would put
            // every page two clicks away, so groups flatten to their icons
            // with a rule between them.
            group.label && !sidebarCollapsed ? (
              <SidebarGroupSection key={group.label} group={group} />
            ) : (
              <div
                key={group.label ?? 'ungrouped'}
                className={cn(
                  sidebarCollapsed && index > 0 && 'mt-1 border-t pt-1',
                  'space-y-0.5',
                )}
              >
                {group.items.map((item) => (
                  <SidebarNavItem key={item.href} item={item} collapsed={sidebarCollapsed} />
                ))}
              </div>
            ),
          )}
        </nav>

        {/* User info */}
        {user && !sidebarCollapsed && (
          <div className="border-t p-3">
            <div className="flex items-center gap-3">
              <div className="flex size-8 items-center justify-center rounded-full bg-primary-50 text-xs font-semibold text-primary">
                {user.firstName[0]}{user.lastName[0]}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-text-heading">
                  {user.firstName} {user.lastName}
                </p>
                <p className="truncate text-[11px] text-text-body">
                  {getRoleDisplayName(activeRole ?? user.role)}
                </p>
              </div>
            </div>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
}

/** Does the current route sit inside this item, at any depth? */
function itemHoldsRoute(item: NavItem | NavSubItem, pathname: string): boolean {
  if (pathname === item.href) return true;
  const children = item.children ?? [];
  return children.some(
    (child) => pathname.startsWith(child.href) || itemHoldsRoute(child, pathname),
  );
}

/**
 * A collapsible group of nav items. Purely a container: clicking it expands,
 * it never navigates, because a group has no page of its own. It starts open
 * when the current route is inside it, and an explicit toggle overrides that
 * for as long as the sidebar stays mounted.
 */
function SidebarGroupSection({ group }: { group: NavGroup }) {
  const location = useLocation();
  const [toggled, setToggled] = useState<boolean | null>(null);
  const holdsRoute = group.items.some((item) =>
    itemHoldsRoute(item, location.pathname),
  );
  const open = toggled ?? holdsRoute;
  const Icon = group.icon;

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setToggled(!open)}
        aria-expanded={open}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[11px] font-semibold uppercase tracking-wide transition-colors',
          holdsRoute
            ? 'text-sidebar-accent-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
      >
        {Icon && <Icon className="size-4 shrink-0" />}
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown
          className={cn('size-3.5 transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div className="mt-0.5 space-y-0.5 border-l ml-4 pl-1.5">
          {group.items.map((item) => (
            <SidebarNavItem key={item.href} item={item} collapsed={false} />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarNavItem({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => {
    if (!item.children) return false;
    return item.children.some((child) => location.pathname.startsWith(child.href));
  });

  const isActive = location.pathname === item.href ||
    item.children?.some((child) => location.pathname.startsWith(child.href));
  const Icon = item.icon;
  const hasChildren = item.children && item.children.length > 0;

  // Collapsed mode — icon only with tooltip
  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <NavLink
            to={item.children?.[0]?.href ?? item.href}
            className={cn(
              'flex items-center justify-center rounded-lg p-2.5 transition-colors',
              isActive
                ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            {Icon && <Icon className="size-5" />}
          </NavLink>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-foreground text-white">
          {item.label}
        </TooltipContent>
      </Tooltip>
    );
  }

  // No children — simple link
  if (!hasChildren) {
    return (
      <NavLink
        to={item.href}
        className={({ isActive: linkActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            linkActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )
        }
      >
        {Icon && <Icon className="size-[18px] shrink-0" />}
        <span>{item.label}</span>
        {item.badge !== undefined && item.badge > 0 && (
          <Badge variant="destructive" className="ml-auto size-5 justify-center rounded-full p-0 text-[10px]">
            {item.badge}
          </Badge>
        )}
      </NavLink>
    );
  }

  // With children — collapsible section
  return (
    <div>
      <button
        onClick={() => {
          const firstChild = item.children?.[0];
          if (!open && firstChild) {
            // Opening: navigate to first child + expand
            void navigate(firstChild.href);
            setOpen(true);
          } else {
            setOpen(!open);
          }
        }}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-sidebar-accent/50 text-sidebar-accent-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-foreground',
        )}
      >
        {Icon && <Icon className="size-[18px] shrink-0" />}
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={cn('size-4 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="ml-7 mt-0.5 space-y-0.5 border-l pl-3">
          {(item.children ?? []).map((child) => (
            <SidebarSubItem key={child.href} child={child} />
          ))}
        </div>
      )}
    </div>
  );
}

// One level of nested sub-items — when a NavSubItem declares its own
// `children`, render a small collapsible inline group instead of a direct
// link. Used by the admin Mine Operations "Integrity" group so the two
// integrity pages stay together without taking two flat slots.
function SidebarSubItem({ child }: { child: NavSubItem }) {
  const location = useLocation();
  const hasChildren = !!child.children?.length;

  const [open, setOpen] = useState(() =>
    hasChildren
      ? (child.children ?? []).some((gc) => location.pathname.startsWith(gc.href))
      : false,
  );

  if (!hasChildren) {
    return (
      <NavLink
        to={child.href}
        // `end` forces exact-match so a parent path like
        // `/admin/safety/environment` does not stay highlighted when the
        // user is on `/admin/safety/environment/incidents`.
        end
        className={({ isActive: childActive }) =>
          cn(
            'flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors',
            childActive
              ? 'text-primary font-medium bg-primary-50/50'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent',
          )
        }
      >
        <span>{child.label}</span>
        {child.badge !== undefined && child.badge > 0 && (
          <Badge variant="destructive" className="ml-auto size-4 justify-center rounded-full p-0 text-[9px]">
            {child.badge}
          </Badge>
        )}
      </NavLink>
    );
  }

  const groupActive = (child.children ?? []).some((gc) =>
    location.pathname.startsWith(gc.href),
  );

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors',
          groupActive
            ? 'text-primary font-medium'
            : 'text-muted-foreground hover:text-foreground hover:bg-accent',
        )}
      >
        <span className="flex-1 text-left">{child.label}</span>
        <ChevronDown
          className={cn('size-3 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <div className="ml-3 mt-0.5 space-y-0.5 border-l pl-2">
          {(child.children ?? []).map((gc) => (
            <NavLink
              key={gc.href}
              to={gc.href}
              end
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-md px-2 py-1.5 text-[12.5px] transition-colors',
                  isActive
                    ? 'text-primary font-medium bg-primary-50/50'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )
              }
            >
              <span>{gc.label}</span>
              {gc.badge !== undefined && gc.badge > 0 && (
                <Badge variant="destructive" className="ml-auto size-4 justify-center rounded-full p-0 text-[9px]">
                  {gc.badge}
                </Badge>
              )}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
